import React, { useState, useEffect } from "react";
import {Box, Card, IconButton, Typography} from "@mui/material";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid,
} from "recharts";
import { http } from "../../../../../clients/http/http.client";
import { HTTP_ENDPOINTS } from "../../../../../clients/clients.constants";
import Loader from "../../../../Inputs/Loader/Loader";
import { useSelector } from "react-redux";
import {makeSafeKey} from "../../../../../utils";
import {Download} from "@mui/icons-material";
import ExportModal from "../../../../general/ExportModal";
import axios from "axios";
import {useLocation} from "react-router-dom";

const { EQUIPMENT_BY_LOCATION, EQUIPMENT_LOCATIONS } = HTTP_ENDPOINTS;

// const COLORS = ["#F1B6DA", "#E8E685", "#B789D8"];
const COLORS = ["#F1B6DA", "#E8E685", "#B789D8", "#89D8C2", "#E89585", "#B6CFF1"];


const processData = (data) => {
    const keyMap = {};

    const processedData = data.map((item) => {
        const newItem = {};
        Object.keys(item).forEach((key) => {
            if (key === 'location') {
                newItem[key] = item[key];
            } else {
                const safeKey = makeSafeKey(key);
                newItem[safeKey] = item[key];
                keyMap[safeKey] = key;
            }
        });
        return newItem;
    });

    return { processedData, keyMap };
};

const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        const payloadItem = payload[0];
        const { name, value, color } = payloadItem || {};
        return (
            <Box
                sx={{
                    backgroundColor: "#1B3665",
                    color: "#FFFFFF",
                    borderRadius: "8px",
                    padding: "8px 12px",
                    textAlign: "center",
                    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.25)",
                }}
            >
                <Typography
                    sx={{
                        fontSize: "16px",
                        fontWeight: "600",
                        color,
                        marginBottom: "4px",
                    }}
                >
                    {value}
                </Typography>
                <Typography sx={{ fontSize: "12px", fontWeight: "400" }}>
                    {name}
                </Typography>
            </Box>
        );
    }
    return null;
};

const CustomLegend = ({ dataKeys, keyMap }) => {
    return (
        <Box
            sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: "16px",
                alignItems: "center",
                marginTop: "10px",
                marginLeft: "60px",
            }}
        >
            {dataKeys.map((key, index) => (
                <Box
                    key={index}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                    }}
                >
                    <Box
                        sx={{
                            width: "12px",
                            height: "12px",
                            backgroundColor: COLORS[index % COLORS.length],
                            borderRadius: "4px",
                        }}
                    />
                    <Typography
                        sx={{
                            fontSize: "12px",
                            color: "#1B3665",
                            fontWeight: "400",
                        }}
                    >
                        {keyMap[key]}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
};

const LocationCard = ({ fixtures, matHandling }) => {
    const [data, setData] = useState([]);
    const [dataKeys, setDataKeys] = useState([]);
    const [keyMap, setKeyMap] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [cancelTokenSource, setCancelTokenSource] = useState(null);
    const [isExporting, setIsExporting] = useState(false);
    const [exportProgress, setExportProgress] = useState(0);
    const [fileGenerationLoading, setIsFileGenerationLoading] = useState(false);
    const [generatedFiles, setGeneratedFiles] = useState(null);
    const location = useLocation();
    const { search } = useLocation();

    const siteIdStore = useSelector((state) => state.siteId);
    const user = useSelector((state) => state.user);



  const buildQueryParams = () => {
    let params = new URLSearchParams();

    if (siteIdStore.siteId) {
      params.append('siteId', siteIdStore.siteId);
    }

    if (location.search.includes('arrc-inventory')) {
      params.delete('siteId');
      params.append('siteId', location.search.includes('arrc-inventory') ? 1 : siteIdStore.siteId)
    }

    if (fixtures && fixtures.length > 0) {
      const equipmentTypeIds = fixtures.join(',');
      params.append('equipmentTypeIds', equipmentTypeIds);
    }

    if (matHandling && matHandling.length > 0) {
      const locationIds = matHandling.join(',');
      params.append('location', locationIds);
    }

    return params.toString() ? `?${params.toString()}` : '';
  };

  const handleDownloadReports = async (fileType) => {
    // Create a cancel token source for this request
    const source = axios.CancelToken.source();
    setCancelTokenSource(source);

    try {
      setIsFileGenerationLoading(true);
      const filters = buildQueryParams();
      const res = await http.client.get(
        `${HTTP_ENDPOINTS.DOWNLOAD_EQUIPMENT_LOCATIONS_REPORT}/${filters}&fileType=${fileType.toLowerCase()}`,
        {
          onDownloadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              setExportProgress(percentCompleted);
              setIsExporting(true);
            }
          },
          cancelToken: source.token,
        }
      );
      // Process result here if needed
      setExportProgress(100);
    } catch (error) {
      if (axios.isCancel(error)) {
        console.log("Export canceled:", error.message);
      } else {
        console.error("Error during export:", error);
      }
    } finally {
      setIsExporting(false);
      setIsFileGenerationLoading(false);
      setExportProgress(0);
      setCancelTokenSource(null);
      checkGeneratedFile();
    }
  };

  const openModal = async () => {
    setModalOpen(true);
    await checkGeneratedFile();
  };

  const checkGeneratedFile = async () => {
    try {
      const email = user.user.preferred_username;

      const dataType = "equipment_locations";
      const res = await http.client.get(`${HTTP_ENDPOINTS.GENERATED_FILE}?dataType=${dataType}&email=${encodeURIComponent(email)}`);
      if (res.files && res.files.length > 0) {
        setGeneratedFiles(res.files);
      } else {
        setGeneratedFiles([]);
      }
    } catch (error) {
      console.error("Error checking generated file:", error);
      setGeneratedFiles([]);
    }
  };


  const closeModal = () => {
    setModalOpen(false);
  };

  const cancelExport = () => {
    if (cancelTokenSource) {
      cancelTokenSource.cancel("Export canceled by user");
    }
  };

  const showChartTypeName = () => {
      const view = new URLSearchParams(search).get('view');

      const prefix = (() => {
          switch (view) {
              case 'fixtures':
                  return 'Fixtures';
              case 'arrc-inventory':
                  return 'Inventory';
              default:
                  return 'Material';
          }
      })

      return prefix()
  }

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const filters = buildQueryParams();
              console.log('filters',filters);
              const response = await http.client.get(`${EQUIPMENT_BY_LOCATION}${filters}`);

                const apiData = response.data ? response.data : response;

                if (!apiData || !Array.isArray(apiData)) {
                    throw new Error('Invalid API response');
                }

                const { processedData, keyMap } = processData(apiData);

                setData(processedData);

                const equipmentTypes = Object.keys(keyMap);
                setDataKeys(equipmentTypes);
                setKeyMap(keyMap);

                setLoading(false);
            } catch (error) {
                console.error("Error fetching data:", error);
                setError(error.message);
                setLoading(false);
            }
        };
        fetchData();
    }, [siteIdStore.siteId, fixtures, matHandling, location]);

    return (
        <Card
            sx={{
                padding: "20px",
                boxShadow: "0px 4px 40px rgba(226, 236, 249, 0.60)",
                borderRadius: "12px",
                flex: 1,
            }}
        >
            <Box
                sx={{
                    marginBottom: "16px",
                  display: 'flex',
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
            >
                <Typography
                    sx={{
                        fontSize: "22px",
                        fontWeight: "600",
                        color: "#1B3665",
                    }}
                >
                    {`${showChartTypeName()} Handling by Location (Current Status)`}
                </Typography>
              <IconButton color="primary" onClick={openModal}>
                <Download />
              </IconButton>
              <ExportModal
                open={modalOpen}
                onClose={closeModal}
                fileName={`equipment_locations_${Date.now()}`}
                handleDownloadAll={handleDownloadReports}
                onCancelExport={cancelExport}
                isMovements
                progress={exportProgress}
                generatedFiles={generatedFiles}
                fileGenerationLoading={fileGenerationLoading}
                isExporting={isExporting}
              />
            </Box>
            {loading ? (
                <Loader sx={{ height: '262px' }} />
            ) : error ? (
                <Typography>Error: {error}</Typography>
            ) : (
                <>
                    <ResponsiveContainer width="100%" height={200}>
                        <BarChart
                            data={data}
                            margin={{
                                top: 20,
                                right: 20,
                                left: 0,
                                bottom: 0,
                            }}
                        >
                            <CartesianGrid strokeDasharray="5 5" stroke="#E0E0E0" />
                            <XAxis
                                dataKey="location"
                                tick={{
                                    fontSize: 14,
                                    fill: "#A0AEC0",
                                }}
                                axisLine={false}
                                tickLine={false}
                            />
                            <YAxis
                                tick={{
                                    fontSize: 14,
                                    fill: "#A0AEC0",
                                }}
                                axisLine={false}
                                tickLine={false}
                                allowDecimals={false}
                            />
                            <Tooltip
                                shared={false}
                                cursor={{
                                    fill: "rgba(0, 0, 0, 0.05)",
                                }}
                                content={<CustomTooltip />}
                                animationDuration={200}
                            />
                            {dataKeys.map((key, index) => (
                                <Bar
                                    key={key}
                                    dataKey={key}
                                    name={keyMap[key]}
                                    fill={COLORS[index % COLORS.length]}
                                    barSize={30}
                                    radius={[4, 4, 0, 0]}
                                />
                            ))}
                        </BarChart>
                    </ResponsiveContainer>
                    <CustomLegend dataKeys={dataKeys} keyMap={keyMap} />
                </>
            )}
        </Card>
    );
};

export default LocationCard;
